/**
 * /tasks — 抓取任务监控（Phase 5 §5.7）.
 * 显示本周 step_runs 5 阶段执行状态 + 手动触发按钮。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';

import MockBanner from '@/components/MockBanner';
import Panel from '@/components/Panel';
import SocialDisclosureBanner from '@/components/SocialDisclosureBanner';
import SocialRunsTable from '@/components/SocialRunsTable';
import SocialSourceCard from '@/components/SocialSourceCard';
import { api } from '@/lib/api';
import {
  DATA_SOURCES_ENDPOINT,
  SOCIAL_LIST_PARAMS,
  SOCIAL_RUNS_ENDPOINT,
  partitionDataSources,
  type DataSourceRow,
  type SocialRun,
} from '@/lib/social';
import { useWeekStore } from '@/stores/weekStore';

// 9.2g: 数据源监控（从 Overview 迁来,复用 backend N-lite 字段）
interface DataSourceMonitor {
  source_id: string;
  source_name: string;
  source_type: string;
  display_status: string; // 7-label
  fetched: number;
  parsed: number;
  retry_count: number; // Major 1: DataSource 真字段
  crawl_host: string | null; // 'domestic' / 'oversea'
  last_success_time: string | null;
  last_failure_time: string | null;
  failure_reason: string | null;
  latest_runs: { status: string; started_at: string | null; items_parsed: number }[];
  next_scheduled_at: string | null;
}

const MONITOR_STATUS_COLOR: Record<string, string> = {
  成功: 'green',
  部分失败: 'orange',
  无新增: 'default',
  已停用: 'default',
  重构中: 'warning',
  未运行: 'default',
  失败: 'red',
};

const monitorColumns: ColumnsType<DataSourceMonitor> = [
  { title: '数据源', dataIndex: 'source_name', width: 140 },
  { title: '类型', dataIndex: 'source_type', width: 90, render: (t) => <Tag>{t}</Tag> },
  {
    title: '状态',
    dataIndex: 'display_status',
    width: 100,
    render: (s: string) => <Tag color={MONITOR_STATUS_COLOR[s] ?? 'default'}>{s}</Tag>,
  },
  { title: '抓取', dataIndex: 'parsed', width: 80 },
  { title: '重试', dataIndex: 'retry_count', width: 70 },
  { title: '宿主', dataIndex: 'crawl_host', width: 100, render: (h) => h ?? '—' },
  {
    title: '上次成功',
    dataIndex: 'last_success_time',
    width: 160,
    render: (t: string | null) => (t ? new Date(t).toLocaleString('zh-CN') : '—'),
  },
  {
    title: '失败原因',
    dataIndex: 'failure_reason',
    width: 160,
    render: (r: string | null) => r || '—',
  },
  {
    title: '最近执行',
    dataIndex: 'latest_runs',
    width: 90,
    render: (runs: DataSourceMonitor['latest_runs']) => (
      <Tooltip
        title={
          <div className="text-[11px]">
            {runs.length === 0 && <div>无记录</div>}
            {runs.map((r, i) => (
              <div key={i}>
                {r.status} · {r.started_at ? new Date(r.started_at).toLocaleString('zh-CN') : '—'} ·{' '}
                {r.items_parsed}
              </div>
            ))}
          </div>
        }
      >
        <span className="text-blue-600 cursor-pointer">{runs.length} 次</span>
      </Tooltip>
    ),
  },
  { title: '下次计划', dataIndex: 'next_scheduled_at', width: 150, render: (t) => t ?? '—' },
];

interface StepRun {
  id: number;
  week: string;
  stage: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'success' | 'failed';
  error_message: string | null;
  items_processed: number;
  duration_sec: number | null;
}

const STAGE_LABEL: Record<string, string> = {
  crawl: '1. 抓取',
  clean: '2. 清洗',
  nlp: '3. NLP',
  metrics_alerts: '4. 指标 + 预警',
  excel_agent_notify: '5. 周报 + 邮件',
};

const STATUS_COLOR: Record<StepRun['status'], string> = {
  running: 'blue',
  success: 'green',
  failed: 'red',
};

export default function TasksPage() {
  const { week, history } = useWeekStore();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['step-runs', week],
    queryFn: async () => (await api.get<StepRun[]>('/tasks', { params: { week } })).data,
    refetchInterval: 5000, // 5 秒轮询
    retry: false,
  });

  const trigger = useMutation({
    mutationFn: async (skipCrawl: boolean) =>
      api.post('/tasks/trigger', null, { params: { week, skip_crawl: skipCrawl } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['step-runs', week] }),
  });

  // 9.2g: 数据源监控（从 Overview 迁来）。**保持 /data-sources 不带 include_social** → crawl monitor 表不混入 REDFOX（§D2/§D4）。
  const { data: monitorData, isLoading: monitorLoading } = useQuery({
    queryKey: ['data-sources-monitor'],
    queryFn: async () => (await api.get<DataSourceMonitor[]>('/data-sources')).data,
    retry: false,
  });
  const monitorSources = monitorData ?? [];

  // PR3-B(§D2/§5): 独立社交采集子区——源级状态取 include_social=true 的 REDFOX 行，ledger 取 social-runs。
  const { data: socialList } = useQuery({
    queryKey: ['data-sources', 'social-source'],
    queryFn: async () =>
      (await api.get<DataSourceRow[]>(DATA_SOURCES_ENDPOINT, { params: SOCIAL_LIST_PARAMS })).data,
    retry: false,
  });
  const socialSource = partitionDataSources(socialList ?? []).social[0] ?? null;

  const { data: socialRunsData, isLoading: socialRunsLoading } = useQuery({
    queryKey: ['social-runs'],
    queryFn: async () => (await api.get<SocialRun[]>(SOCIAL_RUNS_ENDPOINT)).data,
    retry: false,
  });
  const socialRuns = socialRunsData ?? [];

  const runs = data ?? [];
  const usingMock = !data && !isLoading;

  // 按 stage 分组（同 stage 多次执行取最新一条）
  const latestByStage = new Map<string, StepRun>();
  for (const r of runs) {
    const existing = latestByStage.get(r.stage);
    if (!existing || new Date(r.started_at) > new Date(existing.started_at)) {
      latestByStage.set(r.stage, r);
    }
  }

  return (
    <div className="space-y-5">
      {usingMock && <MockBanner message="tasks 接口暂不可用 — 显示空状态" />}

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-[17px] lg:text-[20px] font-semibold text-slate-900 m-0">抓取任务监控 · {week}</h1>
          <p className="text-[12px] lg:text-[13px] text-slate-500 mt-1 mb-0">
            5 阶段 weekly_run 流水线（cron 自动 + 手动触发），自动轮询 5 秒一次
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Tooltip title="跳过 crawl 阶段，只跑下游">
            <Button icon={<ReloadOutlined />} onClick={() => trigger.mutate(true)} loading={trigger.isPending}>
              重跑下游
            </Button>
          </Tooltip>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => trigger.mutate(false)}
            loading={trigger.isPending}
          >
            手动重跑（含抓取）
          </Button>
        </div>
      </div>

      {/* 9.2j(J1): 近 8 周数据完整度（从 Overview 迁来,复用 weekStore.history） */}
      {history.length > 0 && (
        <Panel title="近 8 周数据完整度">
          <div className="flex flex-wrap items-center gap-2">
            {history.map((item) => (
              <Tag key={item.week} color={item.status === 'ready' ? 'green' : 'default'}>
                {item.week} · {item.status === 'ready' ? '已就绪' : '数据不足/待补采'}
              </Tag>
            ))}
            {history.length < 8 && (
              <span className="text-[12px] text-slate-400">当前可用 {history.length} 周</span>
            )}
          </div>
        </Panel>
      )}

      {/* 9.2g: 数据源监控（从 Overview 迁来,完整字段）+ 9.2j summary */}
      <Panel title="数据源监控（5 源 · 真实状态）">
        {monitorSources.length > 0 &&
          (() => {
            const total = monitorSources.length;
            const failed = monitorSources.filter((s) => s.display_status === '失败').length;
            const inactive = monitorSources.filter((s) =>
              ['已停用', '重构中'].includes(s.display_status),
            ).length;
            return (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[13px] mb-3 pb-3 border-b border-slate-100">
                <span className="text-slate-700">
                  共 <b className="text-slate-900">{total}</b> 个数据源
                </span>
                <span className={failed > 0 ? 'text-red-600' : 'text-emerald-600'}>
                  {failed} 失败
                </span>
                <span className="text-slate-400">{inactive} 停用/重构</span>
              </div>
            );
          })()}
        <Table<DataSourceMonitor>
          loading={monitorLoading}
          dataSource={monitorSources}
          columns={monitorColumns}
          rowKey="source_id"
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Panel>

      {/* PR3-B(§D2): REDFOX 社交采集独立子区——邻接 crawl 监控但语义分离（不混入上方 crawl monitor 表）。 */}
      <Panel title="社交采集监控（REDFOX · 社交信号层）">
        <div className="space-y-4">
          <SocialDisclosureBanner source={socialSource} />
          {socialSource ? (
            <SocialSourceCard source={socialSource} />
          ) : (
            <div className="text-[12px] text-slate-400">社交源状态加载中或暂不可用。</div>
          )}
          <div>
            <div className="text-[13px] font-medium text-slate-700 mb-2">社交采集 run 记录</div>
            <SocialRunsTable runs={socialRuns} loading={socialRunsLoading} />
          </div>
        </div>
      </Panel>

      <Panel title="5 阶段状态">
        <div className="space-y-2">
          {Object.entries(STAGE_LABEL).map(([stage, label]) => {
            const r = latestByStage.get(stage);
            return (
              <div
                key={stage}
                className="grid grid-cols-12 gap-2 items-center px-3 py-2 rounded border border-slate-100 hover:bg-slate-50 text-[13px]"
              >
                <div className="col-span-3 font-medium text-slate-900">{label}</div>
                <div className="col-span-2">
                  {r ? (
                    <Tag color={STATUS_COLOR[r.status]}>{r.status}</Tag>
                  ) : (
                    <span className="text-slate-300">未跑</span>
                  )}
                </div>
                <div className="col-span-2 text-right">
                  {r ? `${r.items_processed} items` : '—'}
                </div>
                <div className="col-span-2 text-right">
                  {r?.duration_sec != null ? `${r.duration_sec.toFixed(2)}s` : '—'}
                </div>
                <div className="col-span-3 text-right text-[11px] text-slate-400">
                  {r ? new Date(r.started_at).toLocaleString('zh-CN') : ''}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel title="完整 step_runs 日志（最新 50 条）">
        <div className="space-y-1 text-[12px]">
          {runs.length === 0 && (
            <div className="text-slate-400 py-4 text-center">本周暂无 step_runs 记录</div>
          )}
          {runs.map((r) => (
            <div
              key={r.id}
              className="grid grid-cols-12 gap-2 items-center px-2 py-1.5 border-b border-slate-100"
            >
              <div className="col-span-2 text-slate-400">#{r.id}</div>
              <div className="col-span-3 font-mono">{r.stage}</div>
              <div className="col-span-2">
                <Tag color={STATUS_COLOR[r.status]}>{r.status}</Tag>
              </div>
              <div className="col-span-2 text-right">{r.items_processed}</div>
              <div className="col-span-3 text-right text-slate-400">
                {new Date(r.started_at).toLocaleString('zh-CN')}
              </div>
              {r.error_message && (
                <div className="col-span-12 text-red-600 bg-red-50 p-2 rounded mt-1 text-[11px] font-mono whitespace-pre-wrap">
                  {r.error_message.slice(0, 500)}
                </div>
              )}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
